import React, { useEffect, useState } from 'react';
import { EyeOutlined, UploadOutlined } from '@ant-design/icons';
import { Typography, Form, Row, Col, Card, Upload, Button, message, Space } from 'antd';
import { editPreClearance, showPreClearance, getPreClearances, uploadDocument } from '@app/api/pre-clearance.api';
import { UploadDocumentResponse } from '@app/models/pre_clearance/PreClearanceResponse';

interface DocumentChecklistProps {
  initialformFields: {
    name: string;
    document_id: string;
    status: string;
    image_url: string;
    document_url?: string;
  }[];
  formFields: {
    name: string;
    document_id: string;
    uploadable_types?: string | string[];
  }[];
  status: string;
  pre_clearance_id: string | null;
}

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_FORMATS = ['.pdf', '.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({
  initialformFields: propInitialFields,
  formFields,
  status,
  pre_clearance_id,
}) => {
  const resolveUrl = (field: { document_url?: string; image_url?: string }) =>
    field.document_url ?? field.image_url ?? '';
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [initialformFields, setInitialformFields] = useState(propInitialFields);

  const clearError = (documentId: string) => {
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[documentId];
      return updated;
    });
  };

  useEffect(() => {
    setInitialformFields(propInitialFields);
  }, [propInitialFields]);

  const validateFile = (file: File, documentId: string): boolean => {
    // Validate MIME type
    if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
      const errorMsg = `Invalid file type. Only ${ACCEPTED_FORMATS.join(', ')} files are allowed.`;
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return false;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const errorMsg = `File size (${fileSizeMB}MB) exceeds the ${MAX_FILE_SIZE_MB}MB limit.`;
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return false;
    }

    // Validate file is not empty
    if (file.size === 0) {
      const errorMsg = 'The selected file is empty. Please choose a valid file.';
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return false;
    }

    return true;
  };

  const handleUpload = async (documentId: string, file: File) => {
    if (!pre_clearance_id || isNaN(Number(pre_clearance_id))) {
      const errorMsg = 'Pre Clearance ID is required.';
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return;
    }

    setUploading((prev) => ({ ...prev, [documentId]: true }));
    clearError(documentId);

    try {
      let preClearanceData;
      try {
        preClearanceData = await editPreClearance(Number(pre_clearance_id));
      } catch {
        throw new Error('Failed to fetch pre-clearance data. Please try again.');
      }

      const user_id = preClearanceData?.pre_clearance?.customer?.user?.id;
      if (!user_id) {
        throw new Error('Failed to retrieve User ID. Please contact support.');
      }

      const matchedDocument = formFields.find((doc) => doc.document_id === documentId);

      const formData = new FormData();
      formData.append('pre_clearance_id', pre_clearance_id);
      formData.append('user_id', user_id.toString());
      formData.append('document_id', documentId);
      formData.append('name', matchedDocument?.name ?? '');
      formData.append('file', file, file.name);
      formData.append('verified', 'false');

      let response: any;
      try {
        response = await uploadDocument(Number(pre_clearance_id), formData);
      } catch (uploadError: any) {
        // Handle specific HTTP errors if available
        const status = uploadError?.response?.status;
        if (status === 413) {
          throw new Error('File is too large for the server. Please upload a smaller file.');
        } else if (status === 415) {
          throw new Error('File type not supported by the server.');
        } else if (status === 401 || status === 403) {
          throw new Error('You are not authorised to upload documents.');
        } else if (status >= 500) {
          throw new Error('Server error occurred. Please try again later.');
        }
        throw new Error('Upload failed. Please check your connection and try again.');
      }

      if (response?.data?.pre_clearance_documents || response?.data?.pc_documents) {
        const uploadedDocs = response?.data?.pre_clearance_documents || response?.data?.pc_documents || [];

        if (!Array.isArray(uploadedDocs)) {
          throw new Error('Invalid document list format');
        }
        const uploadedDoc = uploadedDocs.find((doc: any) => doc.document_id?.trim() === documentId?.trim());

        message.success(response.message ?? `${file.name} uploaded successfully.`);

        // Merge updated doc into existing fields instead of replacing all
        setInitialformFields((prev) =>
          prev.map((field) =>
            field.document_id === documentId
              ? {
                  ...field,
                  image_url: uploadedDoc?.document_url ?? field.image_url,
                  document_url: uploadedDoc?.document_url || URL.createObjectURL(file),
                  status: field.status,
                }
              : field,
          ),
        );

        setFileList((prev) => {
          const updated = { ...prev };

          initialformFields.forEach((field) => {
            const url = resolveUrl(field);
            if (url && !updated[field.document_id]) {
              updated[field.document_id] = [
                {
                  uid: field.document_id,
                  name: field.name,
                  status: 'done',
                  url,
                },
              ];
            }
          });

          return updated;
        });
      } else {
        throw new Error('Upload response was invalid. Please try again.');
      }
    } catch (error: any) {
      const errorMsg = error?.message ?? `Failed to upload ${file.name}. Please try again.`;
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      console.error('Upload error:', error);
    } finally {
      setUploading((prev) => ({ ...prev, [documentId]: false }));
    }
  };

  const handleChange = async (documentId: string, fileInfo: any) => {
    clearError(documentId);

    const validFiles = fileInfo.fileList.filter((file: any) => file.originFileObj);

    if (validFiles.length === 0) {
      const errorMsg = 'No file detected. Please try selecting the file again.';
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return;
    }

    const file: File = validFiles[0].originFileObj;

    if (!(file instanceof File)) {
      const errorMsg = 'Invalid file format. Please select a valid file.';
      setErrors((prev) => ({ ...prev, [documentId]: errorMsg }));
      message.error(errorMsg);
      return;
    }

    // Run validation before updating file list or uploading
    if (!validateFile(file, documentId)) return;

    setFileList((prev) => ({ ...prev, [documentId]: validFiles }));
    await handleUpload(documentId, file);
  };

  const mergedFields = initialformFields.map((field) => ({
    ...field,
    image_url: resolveUrl(field),
    imported: false,
  }));

  formFields.forEach((field) => {
    const existsInInitial = initialformFields.some((initField) => initField.document_id === field.document_id);
    if (!existsInInitial) {
      mergedFields.push({
        ...field,
        imported: true,
        status: 'PENDING_DOCUMENT',
        image_url: '',
      });
    }
  });

  const [fileList, setFileList] = useState<Record<string, any>>(() => {
    const initialFiles: Record<string, any> = {};
    if (initialformFields && Array.isArray(initialformFields)) {
      initialformFields.forEach((field) => {
        const url = resolveUrl(field);
        console.log('INIT field:', field.document_id, '| url:', url); // ← debug
        if (url) {
          initialFiles[field.document_id] = [{ uid: field.document_id, name: field.name, status: 'done', url }];
        }
      });
    }
    console.log('INIT fileList:', initialFiles); // ← debug
    return initialFiles;
  });

  useEffect(() => {
    const newFileList: Record<string, any> = {};
    initialformFields.forEach((field) => {
      const url = resolveUrl(field);
      console.log('EFFECT field:', field.document_id, '| url:', url); // ← debug
      if (url) {
        newFileList[field.document_id] = [{ uid: field.document_id, name: field.name, status: 'done', url }];
      }
    });
    console.log('EFFECT fileList:', newFileList); // ← debug
    setFileList(newFileList);
  }, [initialformFields]);

  return (
    <div className="p-6 bg-gray-50 rounded-lg">
      <Form layout="vertical">
        <Row gutter={[16, 16]}>
          {mergedFields.length > 0 ? (
            mergedFields.map((field) => (
              <Col xs={24} sm={12} key={field.document_id}>
                <Card className="h-full bg-white shadow-sm hover:shadow-md transition-shadow duration-200">
                  <div className="p-4 flex flex-col gap-3">
                    {/* Field Label */}
                    <span className="text-gray-700 font-medium">{field.name}</span>

                    {/* Upload Area */}
                    <div
                      className={`bg-gray-50 rounded-lg border ${
                        errors[field.document_id] ? 'border-red-400' : 'border-gray-200'
                      } p-4`}
                    >
                      <Upload
                        accept={ACCEPTED_FORMATS.join(',')}
                        beforeUpload={() => false}
                        fileList={fileList[field.document_id] ?? []}
                        onChange={(file) => handleChange(field.document_id, file)}
                        showUploadList={false}
                        className="w-full"
                        disabled={status !== 'PENDING_DOCUMENT' && status !== 'PENDING_REVIEW'}
                      >
                        <Space>
                          {(status === 'PENDING_DOCUMENT' || status === 'PENDING_REVIEW') && (
                            <Button size="small" disabled={uploading[field.document_id]}>
                              <UploadOutlined className="text-gray-500" />
                              <span>
                                {uploading[field.document_id]
                                  ? 'Uploading...'
                                  : fileList[field.document_id]?.length > 0
                                  ? 'Re-Upload'
                                  : 'Upload'}
                              </span>
                            </Button>
                          )}

                          {field.image_url && (
                            <Button
                              size="small"
                              type="link"
                              icon={<EyeOutlined />}
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(field.image_url, '_blank', 'noopener,noreferrer');
                              }}
                            >
                              View
                            </Button>
                          )}
                        </Space>
                      </Upload>
                    </div>

                    {/* Hint / Error — always inside the card */}
                    {errors[field.document_id] ? (
                      <span className="text-red-500 text-xs">{errors[field.document_id]}</span>
                    ) : (
                      <span className="text-gray-400 text-xs" style={{ color: '#888' }}>
                        {/* Max {MAX_FILE_SIZE_MB}MB &middot; {ACCEPTED_FORMATS.join(', ')} */}
                      </span>
                    )}
                  </div>
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24}>
              <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-center">No documents available.</div>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
};

export default DocumentChecklist;
